import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { Grid2 as Grid, Stack, Box } from '@mui/material'
import { createTheme, ThemeProvider, useTheme } from '@mui/material/styles'
import useConfig from 'hooks/useConfig'
import { ThemeMode } from 'types/config'
import { PRODUCTS, getSizeTierForCount } from './pricingData'
import type { ProductName } from './pricingData'
import EmployeeSlider from './EmployeeSlider'
import ProductCard from './ProductCard'

// ================================|| PRICING EMBED (for iframe) ||================================ //

const APP_URL = process.env.REACT_APP_AUTH0_CALLBACK_URL || 'https://app.purcent.com'

function PricingEmbed() {
  const { onChangeMode } = useConfig()
  const [searchParams] = useSearchParams()
  const outerTheme = useTheme()
  const embedTheme = useMemo(
    () => createTheme(outerTheme, { typography: { fontFamily: "'DM Sans', sans-serif" } }),
    [outerTheme]
  )

  useEffect(() => {
    onChangeMode(ThemeMode.LIGHT)
  }, [])

  const preselectedProduct = searchParams.get('product') as ProductName | null
  const preselectedUsers = searchParams.get('users')

  const [employeeCount, setEmployeeCount] = useState(() => {
    if (preselectedUsers) {
      const parsed = parseInt(preselectedUsers, 10)
      if (!isNaN(parsed) && parsed >= 1 && parsed <= 300) return parsed
    }
    return 10
  })
  const [highlightedProduct] = useState<ProductName | null>(preselectedProduct)

  const sizeTier = getSizeTierForCount(employeeCount)

  const handleSelect = (product: ProductName) => {
    const params = new URLSearchParams({
      product,
      users: String(employeeCount),
    })
    const url = `${APP_URL}/signup?redirect=select-subscription&${params.toString()}`

    // Break out of iframe — open in new tab
    if (window.top !== window.self) {
      window.open(url, '_blank')
    } else {
      window.location.href = url
    }
  }

  const handleContactUs = () => {
    const subject = encodeURIComponent('Purcent Enterprise Inquiry')
    const body = encodeURIComponent(
      `Hi Purcent team,\n\nI'm interested in learning more about your enterprise plans.\n\nCompany size: ${employeeCount >= 300 ? '300+' : employeeCount} employees\n\nPlease get in touch at your earliest convenience.\n\nThank you`
    )
    const url = `mailto:sales@purcent.com?subject=${subject}&body=${body}`

    if (window.top !== window.self) {
      window.open(url, '_blank')
    } else {
      window.location.href = url
    }
  }

  return (
    <ThemeProvider theme={embedTheme}>
      <Box sx={{ p: { xs: 2, sm: 3 }, backgroundColor: '#fff', minHeight: '100vh' }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%' }}>
            <EmployeeSlider value={employeeCount} onChange={setEmployeeCount} />
          </Box>

          <Grid container spacing={3} justifyContent="center">
            {PRODUCTS.map((product) => (
              <Grid key={product.name} size={{ xs: 12, sm: 6, md: 3 }}>
                <ProductCard
                  product={product}
                  sizeTier={sizeTier}
                  highlighted={
                    highlightedProduct === product.name || (!highlightedProduct && product.name === 'premium')
                  }
                  onSelect={handleSelect}
                  onContactUs={handleContactUs}
                />
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Box>
    </ThemeProvider>
  )
}

export default PricingEmbed
